/**
 * Accessibility Button Fix
 * Automatically adds accessible names to buttons that lack them
 * WCAG 2.1 A 4.1.2 compliance
 * Also fixes nested interactive elements and handles data-url navigation
 */

class AccessibilityButtonFix {
  constructor() {
    this.observer = null;
    this.init();
  }

  init() {
    // Fix existing buttons when page loads
    this.fixAllButtons();
    
    // Fix nested interactive elements
    this.fixNestedInteractiveElements();
    
    // Handle data-url navigation
    this.handleDataUrlNavigation();
    
    // Inject necessary CSS fixes
    this.injectCSS();
    
    // Monitor for dynamically added buttons
    this.startObserving();
    
    // Re-check periodically for dynamic content
    setInterval(() => {
      this.fixAllButtons();
      this.fixNestedInteractiveElements();
      this.handleDataUrlNavigation();
    }, 5000);
    
    // More aggressive scanning every 10 seconds
    setInterval(() => {
      this.forceFixAllViolations();
    }, 10000);
    
    // Force fix after page fully loads
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.forceFixAllViolations();
      }, 1000);
    });
    
    // Force fix on route changes (for SPAs)
    let lastUrl = location.href;
    new MutationObserver(() => {
      const url = location.href;
      if (url !== lastUrl) {
        lastUrl = url;
        setTimeout(() => {
          this.forceFixAllViolations();
        }, 500);
      }
    }).observe(document, { subtree: true, childList: true });
  }

  handleDataUrlNavigation() {
    // Handle buttons with data-url attributes for navigation
    const dataUrlButtons = document.querySelectorAll('button[data-url]:not([data-url-handled])');
    
    dataUrlButtons.forEach(button => {
      button.setAttribute('data-url-handled', 'true');
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const url = button.getAttribute('data-url');
        if (url && url !== '#') {
          window.location.href = url;
        }
      });
    });
  }

  fixAllButtons() {
    const buttons = document.querySelectorAll('button');
    buttons.forEach(button => this.fixButton(button));
    
    // Also check elements with role="button"
    const buttonRoles = document.querySelectorAll('[role="button"]');
    buttonRoles.forEach(element => this.fixButton(element));
  }

  fixButton(button) {
    if (this.hasAccessibleName(button)) {
      return; // Button already has an accessible name
    }

    // Try to determine an appropriate accessible name
    const accessibleName = this.generateAccessibleName(button);
    
    if (accessibleName) {
      button.setAttribute('aria-label', accessibleName);
      console.log('ADA Fix: Added aria-label to button:', accessibleName);
    }
  }

  hasAccessibleName(button) {
    // Check if button has any form of accessible name
    if (button.hasAttribute('aria-label') && button.getAttribute('aria-label').trim()) {
      return true;
    }
    
    if (button.hasAttribute('aria-labelledby')) {
      return true;
    }
    
    if (button.hasAttribute('title') && button.getAttribute('title').trim()) {
      return true;
    }
    
    // Check for text content
    const textContent = this.getButtonTextContent(button);
    if (textContent && textContent.length > 0) {
      return true;
    }
    
    // Check for alt text on images inside button
    const img = button.querySelector('img[alt]');
    if (img && img.getAttribute('alt').trim()) {
      return true;
    }
    
    return false;
  }

  getButtonTextContent(button) {
    // Clone the button to avoid modifying the original
    const clone = button.cloneNode(true);
    
    // Remove hidden elements
    const hiddenElements = clone.querySelectorAll('[aria-hidden="true"], .visually-hidden, .hidden');
    hiddenElements.forEach(el => el.remove());
    
    // Remove icon elements that don't have text
    const icons = clone.querySelectorAll('svg, i[class*="icon"], .icon');
    icons.forEach(icon => {
      if (!icon.textContent.trim()) {
        icon.remove();
      }
    });
    
    return clone.textContent.trim();
  }

  generateAccessibleName(button) {
    // Try different strategies to generate an accessible name
    
    // 1. Check for data attributes that might contain meaningful names
    const dataAttributes = ['data-action', 'data-title', 'data-label', 'data-name'];
    for (const attr of dataAttributes) {
      const value = button.getAttribute(attr);
      if (value && value.trim()) {
        return this.formatAccessibleName(value);
      }
    }
    
    // 2. Check parent elements for context
    const parentContext = this.getParentContext(button);
    if (parentContext) {
      return parentContext;
    }
    
    // 3. Check for icon classes to determine function
    const iconFunction = this.getIconFunction(button);
    if (iconFunction) {
      return iconFunction;
    }
    
    // 4. Check CSS classes for semantic meaning
    const classFunction = this.getClassFunction(button);
    if (classFunction) {
      return classFunction;
    }
    
    // 5. Check button position/context
    const contextFunction = this.getContextFunction(button);
    if (contextFunction) {
      return contextFunction;
    }
    
    // 6. Default fallback
    return 'Button';
  }

  formatAccessibleName(text) {
    return text.replace(/[-_]/g, ' ')
               .replace(/([a-z])([A-Z])/g, '$1 $2')
               .toLowerCase()
               .replace(/\b\w/g, l => l.toUpperCase())
               .trim();
  }

  getParentContext(button) {
    // Look for nearby text that might describe the button
    const parent = button.closest('[data-section], .card, .product, .collection');
    if (parent) {
      const heading = parent.querySelector('h1, h2, h3, h4, h5, h6');
      if (heading && heading.textContent.trim()) {
        return `${heading.textContent.trim()} button`;
      }
    }
    
    // Check for form context
    const form = button.closest('form');
    if (form) {
      const formName = form.getAttribute('name') || form.getAttribute('id');
      if (formName) {
        return `${this.formatAccessibleName(formName)} button`;
      }
    }
    
    return null;
  }

  getIconFunction(button) {
    const iconElements = button.querySelectorAll('svg, i, .icon');
    
    for (const icon of iconElements) {
      // Check for common icon patterns
      const iconClass = icon.className.toLowerCase();
      const iconId = icon.id.toLowerCase();
      const iconContent = (iconClass + ' ' + iconId).toLowerCase();
      
      if (iconContent.includes('close') || iconContent.includes('×')) return 'Close';
      if (iconContent.includes('search')) return 'Search';
      if (iconContent.includes('menu') || iconContent.includes('hamburger')) return 'Menu';
      if (iconContent.includes('cart')) return 'Cart';
      if (iconContent.includes('plus') || iconContent.includes('add')) return 'Add';
      if (iconContent.includes('minus') || iconContent.includes('remove')) return 'Remove';
      if (iconContent.includes('edit')) return 'Edit';
      if (iconContent.includes('delete')) return 'Delete';
      if (iconContent.includes('save')) return 'Save';
      if (iconContent.includes('share')) return 'Share';
      if (iconContent.includes('play')) return 'Play';
      if (iconContent.includes('pause')) return 'Pause';
      if (iconContent.includes('next')) return 'Next';
      if (iconContent.includes('prev') || iconContent.includes('previous')) return 'Previous';
      if (iconContent.includes('up') || iconContent.includes('arrow-up')) return 'Up';
      if (iconContent.includes('down') || iconContent.includes('arrow-down')) return 'Down';
      if (iconContent.includes('filter')) return 'Filter';
      if (iconContent.includes('sort')) return 'Sort';
    }
    
    return null;
  }

  getClassFunction(button) {
    const className = button.className.toLowerCase();
    
    if (className.includes('quantity__button')) {
      if (className.includes('minus')) return 'Decrease quantity';
      if (className.includes('plus')) return 'Increase quantity';
      return 'Adjust quantity';
    }
    
    if (className.includes('cart-remove')) return 'Remove from cart';
    if (className.includes('drawer__close')) return 'Close drawer';
    if (className.includes('modal__close')) return 'Close modal';
    if (className.includes('slider-button')) {
      if (className.includes('prev')) return 'Previous slide';
      if (className.includes('next')) return 'Next slide';
      return 'Navigate slides';
    }
    
    if (className.includes('filter')) return 'Filter products';
    if (className.includes('sort')) return 'Sort products';
    if (className.includes('wishlist')) return 'Add to wishlist';
    if (className.includes('compare')) return 'Compare products';
    
    // Shopify-specific patterns
    if (className.includes('shopify-payment-button')) return 'Complete purchase';
    if (className.includes('quick-add')) return 'Quick add to cart';
    if (className.includes('product__media-toggle')) return 'View product image';
    if (className.includes('deferred-media__poster')) return 'Play media';
    if (className.includes('share-button')) return 'Share product';
    if (className.includes('newsletter-form__button')) return 'Subscribe to newsletter';
    if (className.includes('facet')) return 'Filter option';
    if (className.includes('collection-filter')) return 'Filter products';
    if (className.includes('thumbnail')) return 'View image';
    if (className.includes('variant-picker')) return 'Select option';
    
    return null;
  }

  getContextFunction(button) {
    // Check parent containers for context clues
    const quantityContainer = button.closest('.quantity, quantity-input');
    if (quantityContainer) {
      if (button.getAttribute('name') === 'minus') return 'Decrease quantity';
      if (button.getAttribute('name') === 'plus') return 'Increase quantity';
    }
    
    const cartContainer = button.closest('.cart, cart-drawer');
    if (cartContainer && button.querySelector('[class*="remove"]')) {
      return 'Remove item from cart';
    }
    
    const productContainer = button.closest('.product, .card-product');
    if (productContainer) {
      return 'Product action';
    }
    
    return null;
  }

  startObserving() {
    // Create a MutationObserver to watch for dynamically added buttons
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              // Check if the added node is a button
              if (node.tagName === 'BUTTON' || node.getAttribute('role') === 'button') {
                this.fixButton(node);
              }
              
              // Check for buttons within the added node
              const buttons = node.querySelectorAll ? node.querySelectorAll('button, [role="button"]') : [];
              buttons.forEach(button => this.fixButton(button));
              
              // Check for nested interactive elements
              this.fixNestedInteractiveElements();
              
              // Handle data-url navigation for new elements
              this.handleDataUrlNavigation();
            }
          });
        }
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  fixNestedInteractiveElements() {
    // Find all anchor elements that contain buttons or elements with button classes
    const problematicElements = new Set();
    
    // Pattern 1: <a><button></a>
    const anchorsWithButtons = document.querySelectorAll('a button, a [role="button"]');
    anchorsWithButtons.forEach(button => {
      const parentAnchor = button.closest('a');
      if (parentAnchor) {
        problematicElements.add({ parent: parentAnchor, child: button, type: 'anchor-button' });
      }
    });
    
    // Pattern 2: <button><a></button>
    const buttonsWithAnchors = document.querySelectorAll('button a, [role="button"] a');
    buttonsWithAnchors.forEach(anchor => {
      const parentButton = anchor.closest('button, [role="button"]');
      if (parentButton) {
        problematicElements.add({ parent: parentButton, child: anchor, type: 'button-anchor' });
      }
    });
    
    // Pattern 3: <a><span class="button"></span></a> (pseudo-buttons)
    const anchorsWithButtonClasses = document.querySelectorAll('a .button:not(button), a [class*="button"]:not(button)');
    anchorsWithButtonClasses.forEach(element => {
      const parentAnchor = element.closest('a');
      if (parentAnchor && element.className.includes('button')) {
        // Check if this element looks like a button semantically
        if (this.looksLikeButton(element)) {
          problematicElements.add({ parent: parentAnchor, child: element, type: 'anchor-pseudo-button' });
        }
      }
    });
    
    // Pattern 4: <button><span class="link"></span></button> (pseudo-links)
    const buttonsWithLinkClasses = document.querySelectorAll('button .link:not(a), button [class*="link"]:not(a)');
    buttonsWithLinkClasses.forEach(element => {
      const parentButton = element.closest('button');
      if (parentButton && element.className.includes('link')) {
        problematicElements.add({ parent: parentButton, child: element, type: 'button-pseudo-link' });
      }
    });
    
    // Fix all found problematic elements
    problematicElements.forEach(item => {
      this.fixNestedInteractiveElement(item.parent, item.child, item.type);
    });
  }

  looksLikeButton(element) {
    const className = element.className.toLowerCase();
    const buttonKeywords = ['button', 'btn', 'clickable', 'interactive'];
    return buttonKeywords.some(keyword => className.includes(keyword));
  }

  fixNestedInteractiveElement(parent, child, type = 'unknown') {
    try {
      // Strategy 1: Convert anchor with pseudo-button to actual button
      if (type === 'anchor-pseudo-button' && parent.tagName === 'A') {
        const href = parent.getAttribute('href');
        const button = document.createElement('button');
        
        // Copy classes and attributes
        button.className = parent.className;
        Array.from(parent.attributes).forEach(attr => {
          if (!['href', 'onclick'].includes(attr.name)) {
            button.setAttribute(attr.name, attr.value);
          }
        });
        
        // Add navigation functionality
        if (href && href !== '#') {
          button.setAttribute('data-url', href);
          button.addEventListener('click', (e) => {
            e.preventDefault();
            window.location.href = href;
          });
        }
        
        button.innerHTML = parent.innerHTML;
        parent.parentNode.replaceChild(button, parent);
        
        console.log('ADA Fix: Converted anchor with pseudo-button to actual button');
        return;
      }
      
      // Strategy 2: If the child has meaningful content, remove the parent's interactivity
      const childText = this.getButtonTextContent(child);
      const parentHref = parent.getAttribute('href');
      
      if (childText && childText.length > 2) {
        // The child has meaningful content, prioritize it
        if (parent.tagName === 'A') {
          // Convert anchor to span and move href to child if needed
          const span = document.createElement('span');
          span.className = parent.className;
          
          // Copy non-interactive attributes
          Array.from(parent.attributes).forEach(attr => {
            if (!['href', 'onclick', 'role'].includes(attr.name)) {
              span.setAttribute(attr.name, attr.value);
            }
          });
          
          // If child is a button and parent had href, add click handler to child
          if (parentHref && parentHref !== '#' && child.tagName === 'BUTTON') {
            child.addEventListener('click', (e) => {
              if (!e.defaultPrevented) {
                window.location.href = parentHref;
              }
            });
          }
          
          parent.parentNode.replaceChild(span, parent);
          span.appendChild(child);
          
          console.log('ADA Fix: Removed nested interactive element (anchor converted to span)');
        }
      } else {
        // Strategy 3: Remove the child's interactivity and keep parent
        if (child.tagName === 'BUTTON') {
          const span = document.createElement('span');
          span.className = child.className;
          span.innerHTML = child.innerHTML;
          
          // Copy non-interactive attributes
          Array.from(child.attributes).forEach(attr => {
            if (!['type', 'onclick', 'role'].includes(attr.name)) {
              span.setAttribute(attr.name, attr.value);
            }
          });
          
          child.parentNode.replaceChild(span, child);
          console.log('ADA Fix: Removed nested interactive element (button converted to span)');
        }
      }
    } catch (error) {
      console.warn('ADA Fix: Could not fix nested interactive element:', error);
    }
  }

  forceFixAllViolations() {
    console.log('ADA Fix: Running comprehensive accessibility fix...');
    
    // Fix all button naming issues
    this.fixAllButtons();
    
    // Aggressively fix nested interactive elements
    this.fixNestedInteractiveElements();
    
    // Handle any remaining data-url buttons
    this.handleDataUrlNavigation();
    
    // Additional aggressive checks
    this.aggressiveNestedElementFix();
    
    console.log('ADA Fix: Comprehensive accessibility fix completed');
  }

  aggressiveNestedElementFix() {
    // Find ANY buttons inside ANY anchor elements (including dynamically generated ones)
    document.querySelectorAll('a').forEach(anchor => {
      const buttons = anchor.querySelectorAll('button, [role="button"], input[type="button"], input[type="submit"]');
      buttons.forEach(button => {
        this.fixNestedInteractiveElement(anchor, button, 'anchor-button');
      });
      
      // Also check for elements that look like buttons
      const pseudoButtons = anchor.querySelectorAll('[class*="button"], [class*="btn"]');
      pseudoButtons.forEach(element => {
        if (element.tagName !== 'BUTTON' && this.looksLikeButton(element)) {
          this.fixNestedInteractiveElement(anchor, element, 'anchor-pseudo-button');
        }
      });
    });
    
    // Find ANY anchors inside ANY button elements
    document.querySelectorAll('button, [role="button"]').forEach(button => {
      const anchors = button.querySelectorAll('a[href]');
      anchors.forEach(anchor => {
        this.fixNestedInteractiveElement(button, anchor, 'button-anchor');
      });
    });
  }

  injectCSS() {
    // Inject CSS to ensure converted elements maintain proper styling
    const style = document.createElement('style');
    style.id = 'accessibility-fix-styles';
    style.textContent = `
      /* Ensure buttons converted from anchors maintain link styling */
      button.underlined-link,
      button.active-facets__button-remove {
        background: none;
        border: none;
        padding: 0;
        color: inherit;
        text-decoration: underline;
        cursor: pointer;
        font-family: inherit;
        font-size: inherit;
      }
      
      /* Ensure facet buttons maintain proper styling */
      button.active-facets__button {
        background: none;
        border: none;
        padding: 0;
        color: inherit;
        cursor: pointer;
        font-family: inherit;
        font-size: inherit;
      }
      
      button.facets__reset {
        background: none;
        border: none;
        padding: 0;
        color: inherit;
        text-decoration: underline;
        cursor: pointer;
        font-family: inherit;
        font-size: inherit;
      }
      
      button.mobile-facets__clear {
        background: none;
        border: none;
        padding: 0;
        color: inherit;
        text-decoration: underline;
        cursor: pointer;
        font-family: inherit;
        font-size: inherit;
      }
      
      /* Ensure no nested interactive elements are visible if our fix missed anything */
      a button,
      a [role="button"],
      button a {
        pointer-events: none !important;
      }
      
      /* Re-enable pointer events for properly fixed elements */
      button[data-url],
      button[data-url-handled] {
        pointer-events: auto !important;
      }
    `;
    
    // Only inject if not already present
    if (!document.getElementById('accessibility-fix-styles')) {
      document.head.appendChild(style);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new AccessibilityButtonFix();
});

// Also initialize immediately if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AccessibilityButtonFix();
  });
} else {
  new AccessibilityButtonFix();
} 